/**
 * `/decisions` — the multi-admin voting board (migration 174).
 *
 * A decision is a question put to both admins: 2–4 options, a quorum rule,
 * one ballot per admin per ROUND. The engine (staff-decisions, W2) opens
 * decisions — `email_correspondent` ones raised by the inbox lane, `ad_hoc`
 * ones raised here — sends ballots over WhatsApp/Telegram, tallies votes and
 * resolves (dispatching a staged action when the winning option carries one)
 * or re-issues a round. Three sections:
 *
 *   Overview  — open / your-vote-needed / resolved / cancelled, studio-wide.
 *   Open      — one row per open decision: options, quorum + kind badges,
 *               per-voter chips (votes are OPEN to both admins by design),
 *               the round history, the viewer's own vote buttons, Cancel.
 *   Recent    — resolved + cancelled decisions with the resolved trail
 *               (winning option, dispatched action + run ref when present).
 *
 * Voting from this page dispatches `cast_staff_vote {ballot_token,
 * option_index}` — the SAME canonical action the WhatsApp/Telegram reply
 * paths drive. The token comes viewer-scoped from the board payload (only
 * the signed-in admin's own ballot carries one; see the server repository),
 * and the viewer's ballot is resolved by matching whoami's email against
 * the round's registry emails. One aggregate fetch, `GET /api/decisions-board`.
 */

import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type DecisionBallot,
  type DecisionsBoardResponse,
  type StaffDecision,
} from "../lib/api";
import { ActionModalButton } from "../components/ActionModalButton";
import { useWhoami } from "../components/DashboardLayout";
import { useDispatch } from "../lib/use-dispatch";
import {
  buildCreateDecisionInput,
  countAwaitingViewer,
  currentRound,
  decisionKindLabel,
  decisionStatusLabel,
  dispatchOutcomeLine,
  findViewerBallot,
  optionLabelAt,
  optionTally,
  pastRounds,
  quorumLabel,
  staffNameById,
  votedViaLabel,
  votesProgress,
  winningOptionLabel,
} from "../lib/decisions-board";
import { studioFormat } from "../lib/studio-tz";

const DATE_OPTS: Intl.DateTimeFormatOptions = { month: "short", day: "2-digit" };
const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmt(iso: string | null, opts: Intl.DateTimeFormatOptions): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(opts).format(d);
}

const fmtDate = (iso: string | null): string => fmt(iso, DATE_OPTS);
const fmtDateTime = (iso: string | null): string => fmt(iso, DATETIME_OPTS);

export function Decisions(): JSX.Element {
  const [data, setData] = useState<DecisionsBoardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const whoami = useWhoami();
  const viewerEmail = whoami.data?.email ?? null;

  /**
   * Lifted out of the mount effect so every mutating control can refetch.
   * `useDispatch`'s automatic `invalidateAfterMutation()` does NOT reach this
   * board — it busts the `resource-cache` keys, and this page fetches with a
   * plain effect — so without an explicit refetch a cast vote, a cancel or a
   * created decision would appear to do nothing.
   */
  const loadBoard = useCallback((): void => {
    // Deliberately does NOT set `loading` — a REFETCH must not blank the
    // page (the early-return below would unmount any open modal). The first
    // load still shows the placeholder because `loading` starts true.
    api
      .decisionsBoard()
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  if (loading) return <p className="empty">Loading decisions…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load the decisions ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const c = data.counts;

  return (
    <div>
      <h1 className="page-title">Decisions</h1>

      <div className="pr-overview">
        <OverviewStat label="Open" value={c.open} />
        <OverviewStat
          label="Your vote needed"
          value={countAwaitingViewer(data.open, viewerEmail)}
        />
        <OverviewStat label="Resolved" value={c.resolved} />
        <OverviewStat label="Cancelled" value={c.cancelled} />
      </div>

      <div className="pr-section-head">
        <h2 className="pr-heading">Open votes</h2>
        <ActionModalButton
          action="create_staff_decision"
          buttonLabel="New decision"
          buttonTitle="Put an ad-hoc question to both admins"
          modalTitle="New decision"
          description="Ballots go out to both admins over WhatsApp/Telegram; votes are open to each other."
          prefill={{ kind: "ad_hoc" }}
          fields={[
            {
              kind: "text",
              name: "title",
              label: "Title",
              required: true,
              maxLength: 200,
              placeholder: "What are we deciding?",
            },
            {
              kind: "textarea",
              name: "context",
              label: "Context",
              maxLength: 2000,
              placeholder: "What the voters need to know…",
            },
            {
              kind: "text",
              name: "option_1",
              label: "Option 1",
              required: true,
              maxLength: 120,
            },
            {
              kind: "text",
              name: "option_2",
              label: "Option 2",
              required: true,
              maxLength: 120,
            },
            {
              kind: "text",
              name: "option_3",
              label: "Option 3 (optional)",
              maxLength: 120,
            },
            {
              kind: "text",
              name: "option_4",
              label: "Option 4 (optional)",
              maxLength: 120,
            },
            {
              kind: "select",
              name: "quorum",
              label: "Quorum",
              required: true,
              defaultValue: "unanimous",
              options: [
                { value: "unanimous", label: "Unanimous — both must agree" },
                { value: "majority", label: "Majority" },
              ],
            },
          ]}
          transformInput={buildCreateDecisionInput}
          submitLabel="Open the vote"
          onDone={loadBoard}
        />
      </div>

      {data.open.length === 0 ? (
        <p className="empty">
          {c.total === 0
            ? "No decisions yet — the inbox lane raises one when a correspondent needs a call, or open one with New decision."
            : "Nothing open. Every decision is resolved or cancelled."}
        </p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <thead>
              <tr>
                <th>Decision</th>
                <th>Kind</th>
                <th>Quorum</th>
                <th>Round</th>
                <th>Ballots</th>
                <th>Your vote</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.open.map((d) => (
                <OpenDecisionRow
                  key={d.id}
                  decision={d}
                  viewerEmail={viewerEmail}
                  onChanged={loadBoard}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pr-section-head">
        <h2 className="pr-heading">Recent</h2>
        <span className="subtle">
          {data.recent.length} decision{data.recent.length === 1 ? "" : "s"}
        </span>
      </div>

      {data.recent.length === 0 ? (
        <p className="empty">Nothing resolved or cancelled yet.</p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <thead>
              <tr>
                <th>Decision</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Outcome</th>
                <th className="pr-num">Rounds</th>
                <th>Closed</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((d) => (
                <RecentDecisionRow key={d.id} decision={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.has_more ? (
        <p className="subtle">
          Showing the most recent decisions — older history is not listed.
        </p>
      ) : null}
    </div>
  );
}

function OverviewStat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}): JSX.Element {
  return (
    <div className="pr-stat">
      <div className="pr-stat-value">{value}</div>
      <div className="pr-stat-label">{label}</div>
    </div>
  );
}

/** One voter chip — name + choice when voted, name + awaiting otherwise. */
function BallotChip({
  ballot: b,
  decision: d,
}: {
  readonly ballot: DecisionBallot;
  readonly decision: StaffDecision;
}): JSX.Element {
  if (b.voted_at === null) {
    return (
      <span className="tag" title="Ballot sent, not voted yet">
        {b.display_name} · awaiting
      </span>
    );
  }
  const via = votedViaLabel(b.voted_via);
  return (
    <span
      className="tag tag-success"
      title={`Voted ${fmtDateTime(b.voted_at)}${via !== null ? ` via ${via}` : ""}`}
    >
      {b.display_name} ✓ {optionLabelAt(d, b.option_index) ?? "—"}
    </span>
  );
}

/** Compact one-line tally for a past round, per-voter (two admins = the tally). */
function roundSummary(d: StaffDecision, ballots: ReadonlyArray<DecisionBallot>): string {
  return ballots
    .map(
      (b) =>
        `${b.display_name}: ${
          b.voted_at === null ? "—" : (optionLabelAt(d, b.option_index) ?? "—")
        }`,
    )
    .join(" · ");
}

function OpenDecisionRow({
  decision: d,
  viewerEmail,
  onChanged,
}: {
  readonly decision: StaffDecision;
  readonly viewerEmail: string | null;
  /** Refetch the board after a row control mutates the decision. */
  readonly onChanged: () => void;
}): JSX.Element {
  const vote = useDispatch<
    { ballot_token: string; option_index: number },
    unknown
  >("cast_staff_vote");

  const round = currentRound(d);
  const ballots = round?.ballots ?? [];
  const mine = findViewerBallot(ballots, viewerEmail);
  const progress = votesProgress(ballots);
  const history = pastRounds(d);

  const castVote = async (optionIndex: number): Promise<void> => {
    if (mine === null || mine.token === null) return;
    const r = await vote.run({
      ballot_token: mine.token,
      option_index: optionIndex,
    });
    if (r.ok) onChanged();
  };

  return (
    <tr>
      <td className="pr-cell-title table-card-title">
        {d.title}
        {d.context !== null && d.context.length > 0 ? (
          <div className="pr-sub dc-muted pr-clamp" title={d.context}>
            {d.context}
          </div>
        ) : null}
        <div className="pr-sub dc-muted">
          {d.options
            .map((o, i) => {
              const label = optionLabelAt(d, i) ?? `Option ${i + 1}`;
              return o.action !== null ? `${label} ⚙` : label;
            })
            .join(" · ")}
        </div>
        {d.source_ref !== null ? (
          <div className="pr-sub dc-muted pr-clamp" title={d.source_ref}>
            from {d.source_ref}
          </div>
        ) : null}
      </td>
      <td data-label="Kind">
        <span className="pr-type-badge">{decisionKindLabel(d.kind)}</span>
      </td>
      <td data-label="Quorum">
        <span className="tag">{quorumLabel(d.quorum)}</span>
      </td>
      <td data-label="Round">
        <div>
          R{d.round}
          <span className="dc-muted">
            {" "}
            · {progress.voted}/{progress.total} voted
          </span>
        </div>
        {d.last_ballot_sent_at !== null ? (
          <div className="pr-sub dc-muted">
            sent {fmtDate(d.last_ballot_sent_at)}
          </div>
        ) : null}
        {history.map((r) => (
          <div
            key={r.round}
            className="pr-sub dc-muted pr-clamp"
            title={optionTally(r.ballots, d.options.length)
              .map((n, i) => `${optionLabelAt(d, i) ?? `Option ${i + 1}`}: ${n}`)
              .join(" · ")}
          >
            R{r.round}: {roundSummary(d, r.ballots)}
          </div>
        ))}
      </td>
      <td data-label="Ballots">
        {ballots.length === 0 ? (
          <span className="dc-muted">No ballots yet</span>
        ) : (
          ballots.map((b) => <BallotChip key={b.id} ballot={b} decision={d} />)
        )}
      </td>
      <td data-label="Your vote">
        {mine === null ? (
          <span
            className="dc-muted"
            title="No ballot in this round matches your login email — vote from WhatsApp/Telegram instead"
          >
            —
          </span>
        ) : mine.voted_at !== null ? (
          <span className="dc-muted">
            Voted: {optionLabelAt(d, mine.option_index) ?? "—"}
          </span>
        ) : mine.token === null ? (
          <span
            className="dc-muted"
            title="Your ballot carries no token on this login — vote from WhatsApp/Telegram instead"
          >
            —
          </span>
        ) : (
          <>
            {d.options.map((o, i) => (
              <button
                key={i}
                type="button"
                className="pr-toggle"
                disabled={vote.loading}
                title={
                  o.action !== null
                    ? `Winning dispatches ${o.action}`
                    : undefined
                }
                onClick={() => void castVote(i)}
              >
                {optionLabelAt(d, i) ?? `Option ${i + 1}`}
              </button>
            ))}
            {vote.error !== null ? (
              <div className="pr-sub tag-error">
                {vote.error.message} ({vote.error.code})
              </div>
            ) : null}
          </>
        )}
      </td>
      <td className="data-td-actions">
        <ActionModalButton
          action="cancel_staff_decision"
          buttonLabel="Cancel"
          buttonClassName="data-row-delete-btn"
          buttonTitle="Withdraw this decision — no more votes, nothing dispatches"
          modalTitle="Cancel this decision"
          description={d.title}
          prefill={{ decision_id: d.id }}
          submitLabel="Cancel decision"
          onDone={onChanged}
        />
      </td>
    </tr>
  );
}

function RecentDecisionRow({
  decision: d,
}: {
  readonly decision: StaffDecision;
}): JSX.Element {
  const winner = winningOptionLabel(d);
  const outcome = dispatchOutcomeLine(d);
  const resolverName = staffNameById(d, d.resolved_by);
  return (
    <tr>
      <td className="pr-cell-title table-card-title">
        {d.title}
        {d.context !== null && d.context.length > 0 ? (
          <div className="pr-sub dc-muted pr-clamp" title={d.context}>
            {d.context}
          </div>
        ) : null}
      </td>
      <td data-label="Kind">
        <span className="pr-type-badge">{decisionKindLabel(d.kind)}</span>
      </td>
      <td data-label="Status">
        <span className={`tag${d.status === "resolved" ? " tag-success" : ""}`}>
          {decisionStatusLabel(d.status)}
        </span>
      </td>
      <td data-label="Outcome" className="pr-cell-prose">
        {d.status === "resolved" && winner !== null ? (
          <>
            <div>{winner}</div>
            {outcome !== null ? (
              <div
                className={`pr-sub pr-clamp ${
                  outcome.tone === "error" ? "tag-error" : "dc-muted"
                }`}
                title={outcome.title}
              >
                {outcome.text}
              </div>
            ) : null}
          </>
        ) : (
          <span className="dc-muted">—</span>
        )}
      </td>
      <td data-label="Rounds" className="pr-num">{d.rounds.length > 0 ? d.rounds.length : d.round}</td>
      <td data-label="Closed" className="pr-lastcontact">
        <div>{fmtDate(d.resolved_at ?? d.updated_at)}</div>
        {resolverName !== null ? (
          <div className="pr-sub dc-muted">by {resolverName}</div>
        ) : null}
      </td>
    </tr>
  );
}
